/**
 * API Client
 *
 * Axios-based HTTP client with interceptors for authentication
 */

import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { config } from '@/config';
import { mockApi } from './mockApi';

/**
 * API client configuration
 */
export interface ApiClientConfig {
  baseURL: string;
  timeout: number;
  retries?: number;
}

/**
 * API error structure
 */
export interface ApiError {
  code: string;
  message: string;
  status: number;
  details?: Record<string, unknown>;
  timestamp: string;
  requestId?: string;
}

/**
 * Error codes
 */
export enum ErrorCode {
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT = 'TIMEOUT',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Create API client instance with interceptors
 */
export function createApiClient(clientConfig?: Partial<ApiClientConfig>): AxiosInstance {
  const appConfig = config();

  const instance = axios.create({
    baseURL: clientConfig?.baseURL ?? appConfig.apiBaseUrl,
    timeout: clientConfig?.timeout ?? appConfig.apiTimeout,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - add auth token
  instance.interceptors.request.use(
    async (requestConfig) => {
      // Mock API Interceptor
      const appConfig = config();
      if (appConfig.mockApi) {
        // We can't actually intercept and return data here in Axios request interceptor
        // instead, we attach a custom adapter or use a different approach.
        // For simplicity with Axios, we can use an adapter.
        requestConfig.adapter = async (config) => {
          const response = await mockApi.handleRequest(config as InternalAxiosRequestConfig);
          if (response) {
            return response;
          }
          // Fallback to default adapter if no mock found (or throw 404)
          throw {
            response: {
              status: 404,
              data: { message: 'Mock endpoint not found' },
            },
          };
        };
      }

      // TODO: Get token from auth store
      // const token = useAuthStore.getState().accessToken;
      // if (token) {
      //   requestConfig.headers.Authorization = `Bearer ${token}`;
      // }
      return requestConfig;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - handle errors
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const apiError = handleApiError(error);

      // Handle 401 - attempt token refresh
      if (error.response?.status === 401) {
        // TODO: Attempt token refresh
        // const refreshed = await useAuthStore.getState().refreshToken();
        // if (refreshed && error.config) {
        //   // Retry the original request
        //   const token = useAuthStore.getState().accessToken;
        //   error.config.headers.Authorization = `Bearer ${token}`;
        //   return instance.request(error.config);
        // }
      }

      return Promise.reject(apiError);
    }
  );

  return instance;
}

/**
 * Handle and normalize API errors
 */
export function handleApiError(error: AxiosError): ApiError {
  const timestamp = new Date().toISOString();

  // Network error
  if (!error.response) {
    return {
      code: ErrorCode.NETWORK_ERROR,
      message: 'Network error. Please check your connection.',
      status: 0,
      timestamp,
    };
  }

  const { status, data } = error.response;

  // Map status codes to error codes
  let code: ErrorCode;
  let message: string;

  switch (status) {
    case 401:
      code = ErrorCode.UNAUTHORIZED;
      message = 'Authentication required. Please log in.';
      break;
    case 403:
      code = ErrorCode.FORBIDDEN;
      message = 'You do not have permission to perform this action.';
      break;
    case 404:
      code = ErrorCode.NOT_FOUND;
      message = 'The requested resource was not found.';
      break;
    case 400:
    case 422:
      code = ErrorCode.VALIDATION_ERROR;
      message = 'Validation error. Please check your input.';
      break;
    case 408:
      code = ErrorCode.TIMEOUT;
      message = 'Request timed out. Please try again.';
      break;
    default:
      if (status >= 500) {
        code = ErrorCode.SERVER_ERROR;
        message = 'Server error. Please try again later.';
      } else {
        code = ErrorCode.UNKNOWN;
        message = 'An unexpected error occurred.';
      }
  }

  // Use server-provided message if available
  const serverMessage = (data as Record<string, unknown>)?.message;
  if (typeof serverMessage === 'string') {
    message = serverMessage;
  }

  return {
    code,
    message,
    status,
    details: data as Record<string, unknown>,
    timestamp,
  };
}

/**
 * Default API client instance
 */
let defaultClient: AxiosInstance | null = null;

/**
 * Get the default API client (singleton)
 */
export function getApiClient(): AxiosInstance {
  if (!defaultClient) {
    defaultClient = createApiClient();
  }
  return defaultClient;
}

export default getApiClient;
